import React, { useMemo, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

export interface AdaptiveActionSplitterProps {
    children?: React.ReactNode;
    /** Maximum buttons shown flat on Desktop before collapsing into overflow dropdown (Rule 3.2.3: 4) */
    maxDesktop?: number;
    /** Maximum buttons shown flat on Mobile before collapsing (Rule 3.2.3: 2) */
    maxMobile?: number;
    className?: string;
    /** Explicit display mode */
    mode?: 'responsive' | 'desktop-only' | 'mobile-only';
}

export interface PartitionedActionElements {
    actionItems: React.ReactNode[];
    persistentOverlays: React.ReactNode[];
}

function isControlledOverlayElement(node: React.ReactNode): boolean {
    if (!React.isValidElement(node)) return false;
    const props = (node.props || {}) as Record<string, unknown>;
    return typeof props.open === 'boolean' || typeof props.isOpen === 'boolean';
}

export function partitionActionElements(children: React.ReactNode): PartitionedActionElements {
    if (!children) return { actionItems: [], persistentOverlays: [] };
    const actionItems: React.ReactNode[] = [];
    const persistentOverlays: React.ReactNode[] = [];

    React.Children.forEach(children, (child) => {
        if (!child) return;
        if (React.isValidElement(child)) {
            const childProps = (child.props || {}) as Record<string, any>;
            if (child.type === React.Fragment) {
                const nested = partitionActionElements(childProps.children);
                actionItems.push(...nested.actionItems);
                persistentOverlays.push(...nested.persistentOverlays);
            } else if (isControlledOverlayElement(child)) {
                persistentOverlays.push(child);
            } else if (
                typeof child.type === 'string' &&
                (child.type === 'div' || child.type === 'span') &&
                childProps.children &&
                !childProps.onClick &&
                !childProps.role &&
                !childProps['data-atomic-action']
            ) {
                const unpacked = partitionActionElements(childProps.children);
                if (unpacked.actionItems.length > 0 || unpacked.persistentOverlays.length > 0) {
                    actionItems.push(...unpacked.actionItems);
                    persistentOverlays.push(...unpacked.persistentOverlays);
                } else {
                    actionItems.push(child);
                }
            } else {
                actionItems.push(child);
            }
        } else {
            actionItems.push(child);
        }
    });

    return { actionItems, persistentOverlays };
}

/** Chữ hiển thị của một nút (gộp các text con) */
function nodeText(node: React.ReactNode, depth = 0): string {
    if (depth > 6 || node == null || typeof node === 'boolean') return '';
    if (typeof node === 'string' || typeof node === 'number') return String(node);
    if (Array.isArray(node)) return node.map((n) => nodeText(n, depth + 1)).join('');
    if (React.isValidElement(node)) return nodeText((node.props as any)?.children, depth + 1);
    return '';
}

function hasPrinterIcon(node: React.ReactNode, depth = 0): boolean {
    if (depth > 6 || !node) return false;
    if (Array.isArray(node)) return node.some((n) => hasPrinterIcon(n, depth + 1));
    if (!React.isValidElement(node)) return false;
    const t: any = node.type;
    if (t && (t.displayName === 'Printer' || t.displayName === 'PrinterIcon')) return true;
    return hasPrinterIcon((node.props as any)?.children, depth + 1);
}

/**
 * Nút in / tải PDF luôn hiện ngoài thanh thao tác, không gom vào [··· Khác] (30/10/2026):
 * nhận biết qua biểu tượng máy in, data-testid có "print"/"pdf", hoặc nhãn bắt đầu bằng "In " / "PDF".
 */
export function isPrintAction(node: React.ReactNode): boolean {
    if (!React.isValidElement(node)) return false;
    const props = (node.props || {}) as Record<string, any>;
    const testId = String(props['data-testid'] || '').toLowerCase();
    if (/print|pdf/.test(testId)) return true;
    if (hasPrinterIcon(props.children)) return true;
    const label = nodeText(props.children).trim();
    return /^(In\s|In$|PDF|Tải PDF)/.test(label);
}

/** Chia nút: giữ thứ tự gốc, luôn giữ nút in + `othersBudget` nút đầu tiên còn lại */
export function splitKeepingPrint(items: React.ReactNode[], max: number, othersBudget: number) {
    if (items.length <= max) return { primary: items, overflow: [] as React.ReactNode[] };
    const printFlags = items.map(isPrintAction);
    let others = 0;
    const primary: React.ReactNode[] = [];
    const overflow: React.ReactNode[] = [];
    items.forEach((item, i) => {
        if (printFlags[i]) primary.push(item);
        else if (others < othersBudget) {
            primary.push(item);
            others += 1;
        } else overflow.push(item);
    });
    return { primary, overflow };
}

const OVERFLOW_ITEM_WRAPPER_CLASS =
    'w-full [&>button]:w-full [&>button]:justify-start [&>button]:border-0 [&>button]:shadow-none [&>button]:text-xs [&>button]:px-2.5 [&>button]:rounded-md [&>button]:bg-transparent [&>button]:hover:bg-slate-100 [&>button:not(.text-rose-600):not(.text-red-600)]:text-slate-700 [&>button.text-rose-600]:hover:bg-rose-50';

/**
 * AdaptiveActionSplitter (Rule 3.2.3 Anti-Overflow Invariant):
 * - Desktop (>= 768px): <= 4 buttons flat; > 4 keeps 3 primary flat + folds remainder into [···] Khác dropdown.
 * - Mobile (< 768px): <= 2 buttons flat; > 2 keeps 1 primary flat + folds remainder into [···] dropdown.
 * - Controlled modal/dialog elements (`open` / `isOpen` boolean prop) are hoisted outside DropdownMenu so closing
 *   the overflow menu never unmounts active confirmation dialogs.
 */
export const AdaptiveActionSplitter: React.FC<AdaptiveActionSplitterProps> = ({
    children,
    maxDesktop = 4,
    maxMobile = 2,
    className,
    mode = 'responsive',
}) => {
    const { actionItems, persistentOverlays } = useMemo(
        () => partitionActionElements(children),
        [children],
    );
    const [desktopMenuOpen, setDesktopMenuOpen] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    if (actionItems.length === 0 && persistentOverlays.length === 0) {
        return null;
    }

    // Desktop: <= maxDesktop nút thì hiện hết; nhiều hơn thì giữ mọi nút in + các nút chính đầu tiên
    // (ít nhất 2 nút chính, tổng thường 4 nút) — nút in không bị gom vào Khác, không đẩy Lập Lệnh SX / Duyệt giao
    const printCount = actionItems.filter(isPrintAction).length;
    const { primary: desktopPrimary, overflow: desktopOverflow } = splitKeepingPrint(
        actionItems, maxDesktop, printCount === 0 ? maxDesktop - 1 : Math.max(2, maxDesktop - printCount),
    );

    // Mobile: giữ nút in + 1 nút chính
    const { primary: mobilePrimary, overflow: mobileOverflow } = splitKeepingPrint(actionItems, maxMobile, 1);

    const renderOverlays = () =>
        persistentOverlays.length > 0 ? (
            <>
                {persistentOverlays.map((overlay, idx) => (
                    <React.Fragment key={`overlay-${idx}`}>{overlay}</React.Fragment>
                ))}
            </>
        ) : null;

    const renderDesktopGroup = (wrapperClass: string) => (
        <div className={cn(wrapperClass, className)}>
            {desktopPrimary.map((item, idx) => (
                <React.Fragment key={idx}>{item}</React.Fragment>
            ))}

            {desktopOverflow.length > 0 && (
                <DropdownMenu open={desktopMenuOpen} onOpenChange={setDesktopMenuOpen}>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-8 px-2.5 gap-1 text-slate-700 hover:text-slate-900 border-slate-200 bg-white hover:bg-slate-50 shadow-2xs font-medium text-xs shrink-0"
                            data-testid="desktop-overflow-menu-btn"
                        >
                            <MoreHorizontal className="h-4 w-4 text-slate-500" />
                            <span>Khác</span>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52 p-1 shadow-lg border-slate-200">
                        <DropdownMenuLabel className="text-[11px] font-bold uppercase tracking-wider text-slate-400 px-2 py-1.5">
                            Thao tác bổ sung ({desktopOverflow.length})
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator className="my-1 bg-slate-100" />
                        <div className="flex flex-col gap-1 p-0.5">
                            {desktopOverflow.map((item, idx) => (
                                <div
                                    key={idx}
                                    onClick={() => setDesktopMenuOpen(false)}
                                    className={cn(
                                        OVERFLOW_ITEM_WRAPPER_CLASS,
                                        '[&>button]:h-8.5 [&>button]:font-normal',
                                    )}
                                >
                                    {item}
                                </div>
                            ))}
                        </div>
                    </DropdownMenuContent>
                </DropdownMenu>
            )}
        </div>
    );

    const renderMobileGroup = () => (
        <div className={cn('flex md:hidden items-center gap-1 shrink-0', className)}>
            {mobilePrimary.map((item, idx) => (
                <React.Fragment key={idx}>{item}</React.Fragment>
            ))}

            {mobileOverflow.length > 0 && (
                <DropdownMenu open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 text-slate-700 hover:text-slate-900 border-slate-200 bg-white hover:bg-slate-50 shadow-2xs shrink-0"
                            data-testid="mobile-overflow-menu-btn"
                        >
                            <MoreHorizontal className="h-4 w-4 text-slate-500" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48 p-1 shadow-lg border-slate-200">
                        <div className="flex flex-col gap-1 p-0.5">
                            {mobileOverflow.map((item, idx) => (
                                <div
                                    key={idx}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className={cn(
                                        OVERFLOW_ITEM_WRAPPER_CLASS,
                                        '[&>button]:h-9 [&>button]:font-medium',
                                    )}
                                >
                                    {item}
                                </div>
                            ))}
                        </div>
                    </DropdownMenuContent>
                </DropdownMenu>
            )}
        </div>
    );

    if (mode === 'desktop-only') {
        return (
            <>
                {renderDesktopGroup('flex items-center gap-1.5 shrink-0')}
                {renderOverlays()}
            </>
        );
    }

    if (mode === 'mobile-only') {
        return (
            <>
                {renderMobileGroup()}
                {renderOverlays()}
            </>
        );
    }

    return (
        <>
            {renderDesktopGroup('hidden md:flex items-center gap-1.5 shrink-0')}
            {renderMobileGroup()}
            {renderOverlays()}
        </>
    );
};

export default AdaptiveActionSplitter;
